import React from 'react';
import i18n from '../i18n/config';
import logger from '../utils/logger';

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    logger.error('[AppErrorBoundary]', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      // Translation keys are preferred; the literal `title`/`subtitle`/
      // `backLabel` props stay supported so existing callers keep working.
      // This is a class component, so `i18n.t` is used directly rather than
      // the `useTranslation` hook — the error screen is transient, it never
      // needs to re-render on a language switch.
      const {
        title,
        titleKey,
        subtitle,
        subtitleKey,
        backHref = '/',
        backLabel,
        backLabelKey,
      } = this.props;
      const resolve = (literal, key, fallbackKey) => {
        if (literal) return literal;
        return i18n.t(key || fallbackKey, { ns: 'errors' });
      };

      return (
        <div className="min-h-[400px] flex items-center justify-center">
          <div className="max-w-md w-full space-y-6 p-8 text-center">
            <svg
              className="mx-auto h-12 w-12 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h2 className="text-2xl font-bold text-gray-900">
              {resolve(title, titleKey, 'boundary.title')}
            </h2>
            <p className="text-sm text-gray-600">
              {resolve(subtitle, subtitleKey, 'boundary.subtitle')}
            </p>
            {this.state.error && (
              <div className="p-3 bg-red-50 rounded-md text-left">
                <p className="text-xs text-red-800 font-mono break-all">
                  {this.state.error.message}
                </p>
              </div>
            )}
            <div className="flex flex-col space-y-3">
              <button
                onClick={this.handleRetry}
                className="btn-primary"
              >
                {i18n.t('boundary.retry', { ns: 'errors' })}
              </button>
              <a href={backHref} className="btn-secondary">
                {resolve(backLabel, backLabelKey, 'boundary.backHome')}
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AppErrorBoundary;
